import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';

import { AdminOverlay } from '@core/components/admin-overlay/admin-overlay';
import { InAppNotification } from '@core/components/in-app-notification/in-app-notification';
import { ProcessingOverlay } from '@core/components/processing-overlay/processing-overlay';
import { Splash } from '@core/components/splash/splash';
import { WebPushBanner } from '@core/components/web-push-banner/web-push-banner';
import { AdminService } from '@core/services/admin/admin';
import { AuthService } from '@core/services/auth/auth';
import { BiometricService } from '@core/services/biometric/biometric';
import { ConsumerHubService } from '@core/services/consumer-hub/consumer-hub';
import { DeepLinkService } from '@core/services/deep-link/deep-link';
import { DeviceService } from '@core/services/device/device';
import { GoogleMapsLoaderService } from '@core/services/google-maps-loader/google-maps-loader';
import { HealthService } from '@core/services/health/health';
import { HistoryService } from '@core/services/history/history';
import { NetworkService } from '@core/services/network/network';
import { NotificationService } from '@core/services/notification/notification';
import { PendoService } from '@core/services/pendo/pendo';
import { PlatformService } from '@core/services/platform/platform';
import { SpatialService } from '@core/services/spatial/spatial';
import { SquareService } from '@core/services/square/square';
import { StorageService } from '@core/services/storage/storage';
import { UiService } from '@core/services/ui/ui';

import * as enTranslation from '../assets/i18n/en.json';

@Component({
  selector: 'yt-root',
  templateUrl: 'app.html',
  styleUrls: ['app.scss'],
  imports: [IonApp, IonRouterOutlet, Splash, AdminOverlay, InAppNotification, WebPushBanner, ProcessingOverlay],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly biometricService = inject(BiometricService);
  private readonly platformService = inject(PlatformService);
  private readonly googleMapsLoaderService = inject(GoogleMapsLoaderService);
  private readonly healthService = inject(HealthService);
  private readonly spatialService = inject(SpatialService);
  private readonly networkService = inject(NetworkService);
  private readonly deepLinkService = inject(DeepLinkService);
  private readonly deviceService = inject(DeviceService);
  private readonly historyService = inject(HistoryService);
  private readonly notificationService = inject(NotificationService);
  private readonly pendoService = inject(PendoService);
  private readonly storageService = inject(StorageService);
  private readonly squareService = inject(SquareService);
  private readonly translateService = inject(TranslateService);
  private readonly adminService = inject(AdminService);
  private readonly consumerHubService = inject(ConsumerHubService);
  private readonly uiService = inject(UiService);

  protected readonly authUser = this.authService.authUser;
  protected readonly processingOverlay = this.uiService.processingOverlay;

  protected readonly initialized = signal<boolean>(false);
  protected readonly showSplash = signal<boolean>(true);
  private readonly tapCount = signal<number>(0);
  private readonly timer = signal<ReturnType<typeof setTimeout> | null>(null);

  ngOnInit(): void {
    this.init();
  }

  private async init() {
    this.translateService.setTranslation('en', enTranslation);
    this.translateService.setFallbackLang('en');
    this.translateService.use('en');
    this.setSafeArea();

    // One-shot, and deliberately not awaited: it deletes the pre-rename invitation keys, one of which
    // holds the invitee's email, names and date of birth with nothing left that reads it. Nothing
    // downstream depends on it having finished, and a device where it fails is no worse off.
    this.storageService
      .purgeLegacyInvitationKeys()
      .catch((err) => console.warn('[App] Legacy invitation key purge failed', err));

    // Preload third-party SDKs in background — these are only needed on map/payment pages
    // and must not block app initialization
    this.googleMapsLoaderService.load().catch((err) => console.warn('[App] Google Maps load failed', err));
    this.squareService.init().catch((err) => console.warn('[App] Square SDK init failed', err));

    // Safety timeout: ensure app becomes interactive even if auth init hangs
    const safetyTimeout = setTimeout(() => {
      if (!this.initialized()) {
        console.warn('[App] Init safety timeout reached — forcing interactive state');
        this.initialized.set(true);
        this.showSplash.set(false);
      }
    }, 10000);

    try {
      await this.biometricService.checkBiometricsAvailable();
      await this.biometricService.login(false);
    } catch (error) {
      console.error('[App] init error', error);
    } finally {
      clearTimeout(safetyTimeout);
      this.initialized.set(true);
      this.showSplash.set(false);
    }
  }

  private setSafeArea() {
    // TODO Add AndroidNotch handling if still necessary
    if (this.platformService.isWeb()) {
      const style = document.documentElement.style;
      style.setProperty('--ion-safe-area-top', '24px');
      style.setProperty('--safe-area-inset-top', '24px');
      style.setProperty('--ion-safe-area-bottom', '24px');
      style.setProperty('--safe-area-inset-bottom', '24px');
    }
  }

  handleTap() {
    // Disable the rapid-tap admin unlock under browser automation (Playwright sets navigator.webdriver),
    // so E2E clicks don't trip the admin overlay.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;

    this.tapCount.set(this.tapCount() + 1);

    // Start or reset timer
    if (this.timer()) clearTimeout(this.timer()!);
    const timer = setTimeout(() => {
      this.tapCount.set(0); // reset if not reached fast enough
    }, 1000);
    this.timer.set(timer);

    // Check unlock
    if (this.tapCount() >= 10) {
      if (this.timer()) clearTimeout(this.timer()!);
      this.tapCount.set(0);
      this.adminService.toggleUnlock(true);
    }
  }
}
